import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Edit, Download, Search, GitBranch, X } from 'lucide-react';
import { toast } from 'react-toastify';
import UserEditModal from './UserEditModal';
import useUsersStore from '@/stores/AdminStore/UsersStore';
import { LoadingSpinner } from '@/Components/common';
import { setReportsTo, getUserDirectory, User as UserType } from '@/services/PagesServices/UsersService';
import './UserDirectoryTab.css';

const UserDirectoryTab: React.FC = () => {
  const navigate = useNavigate();
  const {
    users,
    departments,
    usersPagination,
    searchTerm,
    selectedDepartment,
    selectedStatus,
    currentPage,
    itemsPerPage,
    loadingUsers,
    fetchUsers,
    updateUser,
    setSearchTerm,
    setSelectedDepartment,
    setSelectedStatus,
    setCurrentPage,
    setItemsPerPage,
  } = useUsersStore();

  const [userToEdit, setUserToEdit] = useState<{
    id: string;
    name: string;
    initials: string;
    firstName?: string;
    lastName?: string;
  } | null>(null);

  // Reports-to picker state
  const [reportsToTarget, setReportsToTarget] = useState<UserType | null>(null);
  const [reportsToSearch, setReportsToSearch] = useState('');
  const [reportsToCandidates, setReportsToCandidates] = useState<UserType[]>([]);
  const [reportsToLoading, setReportsToLoading] = useState(false);
  const [reportsToSaving, setReportsToSaving] = useState(false);

  // Debounced candidate fetch when picker is open
  useEffect(() => {
    if (!reportsToTarget) return;
    const t = setTimeout(async () => {
      setReportsToLoading(true);
      try {
        const res = await getUserDirectory({ search: reportsToSearch, page: 1, limit: 25 });
        if (res.success && res.data) {
          setReportsToCandidates(res.data.filter((u) => u.id !== reportsToTarget.id));
        }
      } finally {
        setReportsToLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [reportsToTarget, reportsToSearch]);

  const openReportsToPicker = (user: UserType) => {
    setReportsToTarget(user);
    setReportsToSearch('');
    setReportsToCandidates([]);
  };

  const closeReportsToPicker = () => {
    setReportsToTarget(null);
    setReportsToSearch('');
    setReportsToCandidates([]);
  };

  const handlePickReportsTo = async (managerId: string | null, managerName: string | null) => {
    if (!reportsToTarget) return;
    setReportsToSaving(true);
    try {
      const res = await setReportsTo(reportsToTarget.id, managerId);
      if (res.success) {
        toast.success(
          managerId
            ? `${reportsToTarget.name} now reports to ${managerName}`
            : `Cleared reports-to for ${reportsToTarget.name}`
        );
        closeReportsToPicker();
        fetchUsers({ page: currentPage, limit: itemsPerPage });
      } else {
        toast.error(res.error?.message || 'Failed to update reports-to');
      }
    } finally {
      setReportsToSaving(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers({ page: 1 });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedDepartment, selectedStatus]);

  // Fetch on pagination change
  useEffect(() => {
    fetchUsers({ page: currentPage, limit: itemsPerPage });
  }, [currentPage, itemsPerPage]);

  const handleViewUser = useCallback((userId: string) => {
    navigate(`/admin/users/details/${userId}`);
  }, [navigate]);

  const handleEditUser = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setUserToEdit({
        id: user.id,
        name: user.name,
        initials: user.initials,
      });
    }
  };

  const handleCloseEditModal = () => {
    setUserToEdit(null);
  };

  const handleSaveUser = async (userId: string, data: { firstName: string; lastName: string }) => {
    const result = await updateUser(userId, {
      firstName: data.firstName,
      lastName: data.lastName,
    });

    if (result.success) {
      toast.success(result.message || 'User updated successfully');
      fetchUsers({ page: currentPage, limit: itemsPerPage });
    } else {
      toast.error(result.message || 'Failed to update user');
    }
  };

  const handleExport = () => {
    toast.info('Export functionality coming soon');
  };

  const totalPages = usersPagination?.totalPages || 1;
  const totalCount = usersPagination?.total || 0;

  // Format last login date
  const formatLastLogin = (dateString: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  return (
    <div className="admin-user-directory">
      <div className="admin-user-directory-controls">
        <div className="admin-user-directory-search">
          <Search className="admin-user-directory-search-icon" size={18} />
          <input
            type="text"
            placeholder="Search users by name, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="admin-user-directory-select"
          value={selectedDepartment}
          onChange={(e) => {
            setSelectedDepartment(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Department</option>
          {departments.map((dept) => (
            <option key={dept} value={dept}>
              {dept}
            </option>
          ))}
        </select>
        <select
          className="admin-user-directory-select"
          value={selectedStatus}
          onChange={(e) => {
            setSelectedStatus(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button className="admin-user-directory-export-btn" onClick={handleExport}>
          <Download size={18} />
          Export
        </button>
      </div>

      <div className="admin-user-directory-table-container">
        {loadingUsers ? (
          <div className="admin-user-directory-loading">
            <LoadingSpinner size="md" />
          </div>
        ) : users.length === 0 ? (
          <div className="admin-user-directory-empty">
            <p>No users found</p>
          </div>
        ) : (
          <table className="admin-user-directory-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Status</th>
                <th>Reports To</th>
                <th>Last Login</th>
                <th>Course</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="admin-user-directory-user-cell">
                      <div className="admin-user-directory-avatar">{user.initials}</div>
                      <div className="admin-user-directory-user-info">
                        <span className="admin-user-directory-user-name">{user.name}</span>
                        <span className="admin-user-directory-user-email">{user.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`admin-user-directory-status-badge ${user.status.toLowerCase()}`}>
                      {user.status}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => openReportsToPicker(user)}
                      title="Set who this user reports to"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        background: 'transparent',
                        border: '1px dashed #d1d5db',
                        borderRadius: 6,
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: 12,
                        color: user.reportsToName ? '#374151' : '#6b7280',
                      }}
                    >
                      <GitBranch size={12} />
                      {user.reportsToName || 'Set manager'}
                    </button>
                  </td>
                  <td>{formatLastLogin(user.lastLogin)}</td>
                  <td>
                    <div className="admin-user-directory-course-info">
                      <span className="admin-user-directory-course-completed">{user.coursesCompleted} completed</span>
                      <span className="admin-user-directory-course-active">{user.coursesActive} active</span>
                    </div>
                  </td>
                  <td>
                    <div className="admin-user-directory-actions">
                      <button
                        className="admin-user-directory-action-btn"
                        onClick={() => handleViewUser(user.id)}
                        title="View details"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        className="admin-user-directory-action-btn"
                        onClick={() => handleEditUser(user.id)}
                        title="Edit"
                      >
                        <Edit size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="admin-user-directory-pagination">
          <div className="admin-user-directory-pagination-info">
            <span>Showing</span>
            <select
              className="admin-user-directory-pagination-select"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>of {totalCount}</span>
          </div>

          <div className="admin-user-directory-pagination-buttons">
            <button
              className="admin-user-directory-page-btn"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum = i + 1;
              // Adjust page numbers if we're past page 3
              if (currentPage > 3 && totalPages > 5) {
                pageNum = currentPage - 2 + i;
                if (pageNum > totalPages) pageNum = totalPages - (4 - i);
              }
              if (pageNum < 1 || pageNum > totalPages) return null;
              return (
                <button
                  key={pageNum}
                  className={`admin-user-directory-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className="admin-user-directory-page-btn"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {userToEdit && (
        <UserEditModal
          user={userToEdit}
          onClose={handleCloseEditModal}
          onSave={handleSaveUser}
        />
      )}

      {reportsToTarget && (
        <div
          onClick={closeReportsToPicker}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, width: 480, maxHeight: '80vh',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            }}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>Set "Reports To" for {reportsToTarget.name}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  Currently reporting to: {reportsToTarget.reportsToName || <em>nobody</em>}
                </div>
              </div>
              <button
                onClick={closeReportsToPicker}
                style={{ background: 'transparent', border: 0, cursor: 'pointer', padding: 4 }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: 12, borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: 9, color: '#9ca3af' }} />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={reportsToSearch}
                  onChange={(e) => setReportsToSearch(e.target.value)}
                  style={{
                    width: '100%', padding: '7px 10px 7px 28px',
                    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
                  }}
                />
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 6 }}>
              {reportsToTarget.reportsToId && (
                <button
                  onClick={() => handlePickReportsTo(null, null)}
                  disabled={reportsToSaving}
                  style={{
                    width: '100%', textAlign: 'left', padding: '10px 12px',
                    background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
                    borderRadius: 6, marginBottom: 6, cursor: 'pointer', fontSize: 13,
                  }}
                >
                  Clear reports-to (no manager)
                </button>
              )}
              {reportsToLoading && <div style={{ padding: 16, color: '#6b7280' }}>Loading...</div>}
              {!reportsToLoading && reportsToCandidates.length === 0 && (
                <div style={{ padding: 16, color: '#6b7280', fontSize: 13 }}>
                  {reportsToSearch ? 'No matches found.' : 'Type to search for a manager.'}
                </div>
              )}
              {reportsToCandidates.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handlePickReportsTo(c.id, c.name)}
                  disabled={reportsToSaving}
                  style={{
                    width: '100%', textAlign: 'left', padding: '8px 10px',
                    background: '#fff', border: '1px solid #e5e7eb', color: '#111827',
                    borderRadius: 6, marginBottom: 4, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}
                >
                  <span
                    style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: '#e0e7ff', color: '#3730a3',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 600,
                    }}
                  >
                    {c.initials}
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</span>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>{c.email}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDirectoryTab;
